import { useRef, useEffect, useState, useMemo } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  ZoomControl,
  Circle,
  CircleMarker,
  useMap,
  Tooltip
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { GPS_SVG } from "../../utils/svgToString";

/* =========================================================
   🎛️ DEFENSIVE KEY NORMALIZER HELPERS
========================================================= */
function getLatitude(obj) {
  return obj ? Number(obj.latitude ?? obj.Latitude ?? 0) : 0;
}

function getLongitude(obj) {
  return obj ? Number(obj.longitude ?? obj.Longitude ?? 0) : 0;
}

function getRecordedTime(obj) {
  return obj ? (obj.recorded_at || obj.captured_Date || obj.CapturedDate || obj.timestamp) : "";
}

/* =========================================================
   TELEMETRY PROCESSING ENGINE
========================================================= */
function processTelemetryTrack(rows, filterSettings) {
  if (!rows || !rows.length) return { filteredPoints: [], rawPoints: [], coordinates: [] };
  // console.log("Processing telemetry track with settings:", rows);
  let allPoints = rows
    .map(r => {
      const lat = getLatitude(r);
      const lng = getLongitude(r);
      const rawTime = getRecordedTime(r);
      return {
        id: r.id,
        lat: lat,
        lng: lng,
        accuracy: Number(r.accuracy || r.Accuracy || 0),
        speed: Number(r.speed || r.Speed || 0),
        strategy: r.strategy || r.Strategy || "N/A",
        ts: new Date(rawTime).getTime(),
        rawTime: rawTime,
        rejectedReason: null,
        created_at: r.created_at || 'N/A'
      };
    })
    .filter(p => !isNaN(p.lat) && !isNaN(p.lng) && p.lat !== 0 && p.lng !== 0 && p.ts)
    .sort((a, b) => a.ts - b.ts);

  let optimizedTrack = [];

  for (let i = 0; i < allPoints.length; i++) {
    const current = allPoints[i];

    if (filterSettings.filterAccuracy && current.accuracy > 120) {
      current.rejectedReason = `High Accuracy Radius (${current.accuracy}m > 120m)`;
      continue;
    }

    if (filterSettings.removeJitter && optimizedTrack.length > 0) {
      const prev = optimizedTrack[optimizedTrack.length - 1];
      
      const rad = Math.PI / 180;
      const dLat = (current.lat - prev.lat) * rad;
      const dLon = (current.lng - prev.lng) * rad;
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(prev.lat * rad) * Math.cos(current.lat * rad) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const meters = 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      if (meters < 4 && current.speed === 0) {
        current.rejectedReason = `Stationary Jitter (${meters.toFixed(2)}m deviation @ 0 km/h)`;
        continue;
      }
    }

    optimizedTrack.push(current);
  }

  return {
    filteredPoints: optimizedTrack,
    rawPoints: allPoints,
    coordinates: optimizedTrack.map(p => [p.lat, p.lng])
  };
}

/* =========================================================
   MAP HELPER COMPONENTS
========================================================= */
function FitBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (positions && positions.length > 0) {
      map.fitBounds(L.latLngBounds(positions), { padding: [50, 50] });
    }
  }, [positions, map]);
  return null;
}

function RecenterControl({ targetCenter }) {
  const map = useMap();
  const handleRecenter = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (targetCenter) {
      map.flyTo(targetCenter, 15, { duration: 1 });
    }
  };

  return (
    <div className="leaflet-bottom leaflet-right" style={{ marginBottom: "80px", marginRight: "10px", zIndex: 1000 }}>
      <div className="leaflet-bar leaflet-control">
        <a href="#" onClick={handleRecenter} title="Recenter Map" style={{ backgroundColor: "#fff", width: "30px", height: "30px", lineHeight: "30px", display: "block", textAlign: "center", textDecoration: "none", color: "#333", fontSize: "16px", fontWeight: "bold", cursor: "pointer" }}>🎯</a>
      </div>
    </div>
  );
}

/* =========================================================
   MAIN COMPONENT
========================================================= */
export default function MapView({ 
  pathLocations = [], 
  activeTab = "today",
  liveUser = null 
}) {
  const [inspectedPoint, setInspectedPoint] = useState(null);   
  const [isOpen, setIsOpen] = useState(false); // 🎯 State to handle open/closed status

  const [mapLayers, setMapLayers] = useState({
    drawpoints: false,       
    drawlines: true,        
    filterAccuracy: true,   
    removeJitter: true,     
  });

  const track = useMemo(() => {
    return processTelemetryTrack(pathLocations, mapLayers);
  }, [pathLocations, mapLayers.filterAccuracy, mapLayers.removeJitter]);

  const liveLat = getLatitude(liveUser);
  const liveLng = getLongitude(liveUser);

  const defaultCenter = useMemo(() => {
    if (liveLat && liveLng) return [liveLat, liveLng];
    if (track.filteredPoints.length > 0) {
      const last = track.filteredPoints[track.filteredPoints.length - 1];
      return [last.lat, last.lng];
    }
    return [23.0308, 72.5710];
  }, [track.filteredPoints, liveLat, liveLng]);

  return (
    <div style={{ position: "relative", width: "100%" }}>
      
      {/* ⚙️ SLEEK MICRO DIAGNOSTICS CONTAINER */}
      <div style={{
        position: "absolute",
        top: "12px",
        left: "12px",
        backgroundColor: "white",
        borderRadius: isOpen ? "8px" : "4px",
        boxShadow: "0 1px 5px rgba(0,0,0,0.2)",
        zIndex: 1000,
        fontFamily: "sans-serif",
        overflow: "hidden",
        transition: "all 0.2s ease"
      }}>
        {!isOpen ? (
          // Tiny gear button when collapsed
          <button 
            onClick={() => setIsOpen(true)}
            title="Open Diagnostics"
            style={{
              background: "none", border: "none", width: "32px", height: "32px",
              cursor: "pointer", fontSize: "16px", display: "flex", 
              alignItems: "center", justifyContent: "center"
            }}
          >
            ⚙️
          </button>
        ) : (
          // Clean micro menu when expanded
          <div style={{ padding: "10px", width: "210px", fontSize: "11px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: "bold", borderBottom: "1px solid #eee", paddingBottom: "4px", marginBottom: "6px", color: "#555" }}>
              <span>⚙️ Diagnostics</span>
              <button onClick={() => setIsOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#999", fontWeight: "bold", fontSize: "12px" }}>✕</button>
            </div>
            
            <label style={{ display: "flex", alignItems: "center", marginBottom: "5px", cursor: "pointer" }}>
              <input type="checkbox" checked={mapLayers.drawpoints} onChange={(e) => setMapLayers(prev => ({ ...prev, drawpoints: e.target.checked }))} style={{ marginRight: "6px", width:"auto" }} />
              Show Dots (Nodes)
            </label>
            <label style={{ display: "flex", alignItems: "center", marginBottom: "8px", cursor: "pointer" }}>
              <input type="checkbox" checked={mapLayers.drawlines} onChange={(e) => setMapLayers(prev => ({ ...prev, drawlines: e.target.checked }))} style={{ marginRight: "6px", width:"auto" }} />
              Show Lines (Paths)
            </label>

            <div style={{ borderTop: "1px solid #eee", paddingTop: "6px", marginTop: "4px", fontWeight: "bold", marginBottom: "5px", color: "#555" }}>Filters</div>
            
            <label style={{ display: "flex", alignItems: "center", marginBottom: "4px", cursor: "pointer" }}>
              <input type="checkbox" checked={mapLayers.filterAccuracy} onChange={(e) => setMapLayers(prev => ({ ...prev, filterAccuracy: e.target.checked }))} style={{ marginRight: "6px", width:"auto" }} />
              Accuracy Mask (&gt;120m)
            </label>
            <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
              <input type="checkbox" checked={mapLayers.removeJitter} onChange={(e) => setMapLayers(prev => ({ ...prev, removeJitter: e.target.checked }))} style={{ marginRight: "6px", width:"auto" }} />
              Jitter Mask (&lt;4m @ 0km/h)
            </label>
          </div>
        )}
      </div>

      <MapContainer
        center={defaultCenter}
        zoom={14}
        zoomControl={false}
        style={{ height: "550px", width: "100%", borderRadius: "8px" }}
      >
        <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" />
        <ZoomControl position="bottomright" />
        <RecenterControl targetCenter={defaultCenter} />

        {track.coordinates.length > 0 && <FitBounds positions={track.coordinates} />}

        {/* 🛣️ ROUTE LAYER */}
        {mapLayers.drawlines && track.coordinates.length > 1 && (
          <>
            <Polyline positions={track.coordinates} pathOptions={{ color: "#3b82f6", weight: 8, opacity: 0.15, lineCap: "round" }} />
            <Polyline positions={track.coordinates} pathOptions={{ color: "#2563eb", weight: 3, opacity: 0.85, dashArray: "1, 2" }} />
          </>
        )}

        {/* 🔬 INSTANT DIAGNOSTICS LAYER */}
        {mapLayers.drawpoints && track.rawPoints.map((p, idx) => {
          const isRejected = !!p.rejectedReason;
          return (
            <g key={`diag-${p.id || idx}`}>
              <CircleMarker
                center={[p.lat, p.lng]}
                radius={isRejected ? 4 : 5}
                pathOptions={{
                  fillColor: isRejected ? "#ef4444" : "#10b981", 
                  color: isRejected ? "#b91c1c" : "#047857",
                  weight: 1,
                  fillOpacity: 0.8
                }}
                eventHandlers={{
                  click: () => setInspectedPoint(p)
                }}
              >
                <Tooltip direction="top">
                  <div>
                    <strong>[ID: {p.id}]</strong>
                    <br />
                    <strong>Point #{idx + 1} Status: {isRejected ? "❌ Dropped" : "✅ Passed"}</strong>
                    {isRejected && <div style={{ color: "#ef4444" }}>Reason: {p.rejectedReason}</div>}
                    <div>Accuracy: {p.accuracy}m | Speed: {p.speed} km/h</div>
                    <div>Time: {new Date(p.rawTime).toLocaleTimeString()}</div>
                    <div>Created At: {new Date(p.created_at).toLocaleTimeString()}</div>
                    <div>Strategy: {p.strategy}</div>
                  </div>
                </Tooltip>
              </CircleMarker>

              {inspectedPoint?.id === p.id && (
                <Circle 
                  center={[p.lat, p.lng]} 
                  radius={p.accuracy} 
                  pathOptions={{ color: isRejected ? "#ef4444" : "#3b82f6", weight: 1, fillOpacity: 0.05 }} 
                />
              )}
            </g>
          );
        })}

        {/* FALLBACK STATIONARY MARKER */}
        {track.coordinates.length <= 1 && (
          <Marker position={defaultCenter} icon={L.divIcon({ html: `<div class="gpstracking"><div class="gps-pulse"></div><div>${GPS_SVG}</div></div>`, iconSize: [40, 40], iconAnchor: [20, 20], className: "" })}>
            <Tooltip direction="top" offset={[0, -10]} permanent>
              <strong>Location Locked: {defaultCenter[0].toFixed(4)}, {defaultCenter[1].toFixed(4)}</strong>
            </Tooltip>
          </Marker>
        )}

        {/* TRIP START PIN */}
        {track.coordinates.length > 1 && (
          <Marker position={track.coordinates[0]} icon={L.divIcon({ html: `<div style="background:#10b981; width:14px; height:14px; border-radius:50%; border:2px solid #fff; box-shadow:0 2px 4px rgba(0,0,0,0.3);"></div>`, iconSize: [14, 14], iconAnchor: [7, 7] })}>
            <Tooltip direction="top"><strong>Trip Start</strong></Tooltip>
          </Marker>
        )}

        {/* LIVE POSITION BEACON */}
        {liveLat && liveLng && activeTab === "today" && track.coordinates.length > 1 && (
          <Marker position={[liveLat, liveLng]} icon={L.divIcon({ html: `<div class="gpstracking"><div class="gps-pulse"></div><div>${GPS_SVG}</div></div>`, iconSize: [40, 40], iconAnchor: [20, 20], className: "" })}>
            <Tooltip direction="top" offset={[0, -10]}><strong>Live Connection</strong></Tooltip>
          </Marker>
        )}
      </MapContainer>

      {/* 📊 OPTIONAL INSPECTOR DASHBOARD OVERLAY */}
      {inspectedPoint && (
        <div style={{ position: "absolute", bottom: "20px", left: "20px", background: "white", padding: "12px", borderRadius: "6px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", zIndex: 1000, fontSize: "12px", width: "250px" }}>
          <div style={{ display: "flex", justifyContent: "between", fontWeight: "bold", borderBottom: "1px solid #eee", paddingBottom: "4px", marginBottom: "6px" }}>
            <span>🔍 Telemetry Inspector</span>
            <button onClick={() => setInspectedPoint(null)} style={{ marginLeft: "auto", border: "none", background: "none", cursor: "pointer", color: "#999" }}>✕</button>
          </div>
          <div><strong>Lat/Lng:</strong> {inspectedPoint.lat.toFixed(5)}, {inspectedPoint.lng.toFixed(5)}</div>
          <div><strong>Accuracy:</strong> {inspectedPoint.accuracy} meters</div>
          <div><strong>Speed:</strong> {inspectedPoint.speed} km/h</div>
          <div><strong>Strategy:</strong> {inspectedPoint.strategy}</div>
          {inspectedPoint.rejectedReason ? (
            <div style={{ marginTop: "6px", padding: "4px", background: "#fef2f2", color: "#991b1b", borderLeft: "3px solid #ef4444" }}>
              <strong>Dropped By Engine:</strong><br />{inspectedPoint.rejectedReason}
            </div>
          ) : (
            <div style={{ marginTop: "6px", padding: "4px", background: "#f0fdf4", color: "#166434", borderLeft: "3px solid #10b981" }}>
              <strong>Status:</strong> Active Vector Node
            </div>
          )}
        </div>
      )}
    </div>
  );
}